import encryptedStorage from '@/stores/encryptedStorage';
import { setStorageKey } from '@/utils/setStorageKey';
import { logError, logInfo } from '@/utils/logHandling';

const MIGRATION_VERSION_KEY = 'storageKeyMigrationV1';
const MONGODB_ID_REGEX = /^[0-9a-f]{24}$/i;
const REENCRYPT_PREFIXES = ['sessionBuffer', 'starContainer'];
const REENCRYPT_EXACT_KEYS = new Set([
  'hasSeenNewTemplateModal',
  'newNavAnimationCountButton',
  'newNavAnimationCountNav',
  'accountWasDeleted',
]);

const isUnencryptedKey = (k: string): boolean =>
  MONGODB_ID_REGEX.test(k) ||
  REENCRYPT_PREFIXES.some((prefix) => k.startsWith(prefix)) ||
  REENCRYPT_EXACT_KEYS.has(k);

export const migrateSetStorageKeys = async (): Promise<void> => {
  const storage = encryptedStorage();

  const alreadyMigrated = await storage.getItem(MIGRATION_VERSION_KEY);
  if (alreadyMigrated) {
    return;
  }

  try {
    let keys: readonly string[];
    try {
      keys = await storage.listItems();
    } catch {
      // Fresh install — storage directory doesn't exist yet, nothing to migrate.
      await storage.setItem(MIGRATION_VERSION_KEY, true);
      return;
    }

    const keysToMigrate = keys.filter((k: string) => isUnencryptedKey(k));

    if (keysToMigrate.length === 0) {
      await storage.setItem(MIGRATION_VERSION_KEY, true);
      return;
    }

    logInfo(
      `Storage migration: found ${keysToMigrate.length} unencrypted key(s) to migrate`,
    );

    // Write the flag before processing keys. If the app crashes natively during
    // migration (e.g. the native AES module receives unexpected input), this
    // prevents an infinite crash loop on every subsequent launch. Any keys that
    // weren't migrated before the crash will be handled by the per-key fallback
    // in encryptedStorage.getItem.
    await storage.setItem(MIGRATION_VERSION_KEY, true);

    for (const oldKey of keysToMigrate) {
      try {
        const data: any = await storage.getItem(oldKey);
        if (data === null || data === undefined) {
          await storage.removeItem(oldKey);
          continue;
        }

        if (MONGODB_ID_REGEX.test(oldKey)) {
          // Set data — migrate to prefixed encrypted key, delete old bare-ID key
          if (!data?.exerciseClass) {
            await storage.removeItem(oldKey);
            continue;
          }
          const newKey = setStorageKey(oldKey, data.exerciseClass);
          await storage.setItem(newKey, data);
          await storage.removeItem(oldKey);
        } else {
          // Non-set keys — re-encrypt in place under the same key
          await storage.setItem(oldKey, data);
        }
      } catch (err) {
        logError(`Migration failed for key ${oldKey}, wiping`, err);
        try {
          await storage.removeItem(oldKey);
        } catch {}
      }
    }

    logInfo('Storage migration complete');
  } catch (err) {
    logError('Storage migration encountered an error', err);
  }
};
